import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { cloneDeep } from 'libs/common';
import { SetActions, Loader, Confirm } from 'libs/ui';
import { useStickyHeader } from 'libs/hooks';
import { TableHeader } from './table-header';
import { TableRow } from './table-row';
import { TablePlace, TableCmp, TableBody } from './table.styled';

/**
 * table component
 */
export const Table = (props) => {
  const { theme = '', minWidth = 750, title = '', fields, rows, actions, onRowClick, onRowClickCmp, getRowClass, getRowPopup, updateEntry, setActions, refreshed, refresh, getFilter, clientId } = props;
  const [showConfirm, setShowConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});
  const [selectedAll, setSelectedAll] = useState(false);
  const { tableRef, isSticky } = useStickyHeader();

  /**
   * clear selection on refresh data
   */
  useEffect(() => {
    setSelected({});
    setSelectedAll(false);
  }, [refreshed]);

  if (!rows) {
    return <Loader />;
  }

  if (!rows.count) {
    return null;
  }

  /**
   * delete action with confirm
   * @param row
   * @returns
   */
  const deleteAction = (row) => {
    const action = props.deleteAction;
    if (!action) {
      return;
    }

    let title = 'Удалить запись';
    if (row.title) {
      title += ' ' + row.title;
    }

    setShowConfirm({
      active: true,
      confirmText: title,
      confirmAction: () => {
        action(
          row,
          () => {
            closeConfirm();
          },
          (error) => {
            const out = typeof error === 'object' ? JSON.stringify(error) : error;
            setError(out);
          }
        );
      },
    });
  };

  const closeConfirm = () => {
    setShowConfirm(null);
  };

  /**
   * select all rows
   */
  const selectAll = () => {
    if (selectedAll) {
      setSelected({});
      setSelectedAll(false);
      return;
    }

    const out = cloneDeep(selected);
    rows.items.forEach((row) => {
      out[row.id] = 1;
    });
    setSelected(out);
    setSelectedAll(true);
  };

  /**
   * select row
   */
  const select = (row, active) => {
    const out = cloneDeep(selected);
    const id = row.id;
    if (active) {
      out[id] = 1;
    } else {
      delete out[id];
    }
    setSelected(out);
    setSelectedAll(false);
  };

  /**
   * clear selected
   */
  const clearSelected = () => {
    setSelected({});
    setSelectedAll(false);
  };

  const withCheck = !!setActions && setActions.length > 0;
  const withDelete = !!props.deleteAction;
  const rowParams = { onRowClick, onRowClickCmp, actions, fields, getRowClass, getRowPopup, updateEntry, withCheck, select };
  if (withDelete) {
    rowParams.deleteAction = deleteAction;
  }

  return (
    <>
      <TablePlace>
        <TableCmp ref={tableRef} aria-labelledby={title} style={{ minWidth }} theme={theme} $sticky={isSticky ?? undefined}>
          <TableHeader fields={fields} actions={actions} withCheck={withCheck} selectAll={selectAll} withCheckChecked={selectedAll} withDelete={withDelete} theme={theme} $sticky={isSticky ?? undefined} />
          <TableBody>
            {rows.items.map((row, idx) => {
              const checked = !!selected[row.id];
              return <TableRow key={idx} row={row} {...rowParams} theme={theme} withCheckChecked={checked} />;
            })}
          </TableBody>
        </TableCmp>
      </TablePlace>

      <SetActions getFilter={getFilter} selected={selected} actions={setActions} clear={clearSelected} refresh={refresh} cntAll={rows.count} clientId={clientId} />

      {showConfirm && showConfirm.active && (
        <Confirm error={error} open={true} close={closeConfirm} title={t('grid.Подтвердите действие')} info={showConfirm.confirmText} action={showConfirm.confirmAction} actionTitle={t('grid.Подтвердить')} />
      )}
    </>
  );
};

Table.propTypes = {
  fields: PropTypes.array.isRequired,
  rows: PropTypes.object,
  setActions: PropTypes.array,
  actions: PropTypes.array,
  minWidth: PropTypes.number,
  title: PropTypes.string,
  onRowClick: PropTypes.func,
  onRowClickCmp: PropTypes.func,
  getRowClass: PropTypes.func,
  control: PropTypes.any,
  theme: PropTypes.string,
};
